import { useEffect, useId, useState } from 'react';
import { Button } from '../components/Button/Button';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import { useAddChild } from './useAuth';
import './AddChildDialog.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal that lets a signed-in parent create a child profile.
 *
 * The PIN is short on purpose — kids will type it on a phone — so
 * the brute-force surface is small. Brute-force protection lives at
 * the rate-limiting layer (server-side); this dialog enforces the
 * minimum-length rule per the API.
 *
 * The display name is what the kid sees on their account chip and
 * what the parent picks from the children list. Parent-side it must
 * be unique under that parent (the API enforces this with 409).
 */
export function AddChildDialog({ isOpen, onClose }: Props): JSX.Element | null {
  const titleId = useId();
  const [displayName, setDisplayName] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const addChild = useAddChild();

  useEffect(() => {
    if (!isOpen) {
      setDisplayName('');
      setBirthYear('');
      setPin('');
      setPinConfirm('');
      addChild.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !addChild.isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedName = displayName.trim();
  const yearNum = Number(birthYear);
  const yearValid = Number.isInteger(yearNum) && yearNum >= 1900 && yearNum <= 2100;
  const pinValid = pin.length >= 4 && pin.length <= 8 && /^\d+$/.test(pin);
  const pinMatches = pin === pinConfirm;
  const canSubmit =
    trimmedName.length > 0 && yearValid && pinValid && pinMatches && !addChild.isPending;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    addChild.mutate(
      {
        displayName: trimmedName,
        birthYear: yearNum,
        pin,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div
      className="bl-add-child__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !addChild.isPending) onClose();
      }}
    >
      <div
        className="bl-add-child__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="bl-add-child__title">
          Add a kid
        </h2>
        <p className="bl-add-child__lead">
          Pick a display name (no real name needed), the year your kid was born, and a short PIN.
          The kid signs in by typing your email plus their display name plus the PIN.
        </p>

        <form className="bl-add-child__form" onSubmit={onSubmit}>
          <label className="bl-add-child__field">
            <span>Display name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Avery"
              maxLength={80}
              required
            />
          </label>
          <label className="bl-add-child__field">
            <span>Birth year</span>
            <input
              type="number"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              placeholder="2016"
              min={1900}
              max={2100}
              required
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- RevealableInput renders a real input; lint rule doesn't trace through the custom wrapper */}
          <label className="bl-add-child__field">
            <span>PIN (4–8 digits)</span>
            <RevealableInput
              defaultType="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
              required
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- RevealableInput renders a real input; lint rule doesn't trace through the custom wrapper */}
          <label className="bl-add-child__field">
            <span>Type the PIN again</span>
            <RevealableInput
              defaultType="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
              required
            />
          </label>

          {pin.length > 0 && pinConfirm.length > 0 && !pinMatches && (
            <p className="bl-add-child__warn" role="alert">
              The two PINs don&rsquo;t match.
            </p>
          )}
          {addChild.error && (
            <p className="bl-add-child__error" role="alert">
              {addChild.error.message}
            </p>
          )}

          <div className="bl-add-child__actions">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={addChild.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {addChild.isPending ? 'Adding…' : 'Add kid'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
